import HtmlToReact from 'html-to-react';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import { countryName } from 'common/helpers/addresses';

// eslint-disable-next-line import/no-cycle
import AddCaseToCart from './AddCaseToCart';
import AddToCart from './AddToCart';
import Allocations from './Allocations';
import CollectionProductCount from './CollectionProductCount';
import CompareAtPrice from './CompareAtPrice';
import EventDate from './EventDate';
import EventTime from './EventTime';
import Image from './Image';
import ImageArray from './ImageArray';
import Price from './Price';
import ProductArray from './ProductArray';
import ProductLink from './ProductLink';
import ProductTitle from './ProductTitle';
import Wine from './Wine';
import PriceFilter from './filters/PriceFilter';
import VendorFilter from './filters/VendorFilter';
import WineAppellationFilter from './filters/WineAppellationFilter';
import WineCountryCodeFilter from './filters/WineCountryCodeFilter';
import WineRegionFilter from './filters/WineRegionFilter';
import WineTypeFilter from './filters/WineTypeFilter';
import WineVarietalFilter from './filters/WineVarietalFilter';

export const processingInstructions = (
  product,
  type,
  settings,
  productIndex,
  productCount
) => [
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-images';
    },
    processNode(node, children) {
      const imageTemplate = ReactDOMServer.renderToStaticMarkup(children);
      const newImageTemplate = imageTemplate.replace(/c7not/g, '!');
      return (
        <ImageArray
          key={node.attribs.start || 'key'}
          start={node.attribs.start}
          end={node.attribs.end}
          product={product}
          settings={settings}
          type={type}
          processingImages={processingImages}
        >
          {newImageTemplate}
        </ImageArray>
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-if';
    },
    processNode(node, children) {
      const [key] = Object.keys(node.attribs);
      let isAllowed = false;
      if (!key) {
        return 'Invalid <c7-if> tag';
      }

      // If there is no value to the object key - then test for existance
      if (key.charAt(0) === '!') {
        if (node.attribs[key] === key || node.attribs[key] === '') {
          isAllowed = doesAttributeNotExist(key, product);
        } else {
          // If there is a value to the object key - then test for equality
          isAllowed = doesAttributeNotEqual(key, node.attribs[key], product);
        }
      } else if (node.attribs[key] === key || node.attribs[key] === '') {
        isAllowed = doesAttributeExist(key, product);
      } else {
        // If there is a value to the object key - then test for equality
        isAllowed = doesAttributeEqual(key, node.attribs[key], product);
      }
      if (isAllowed) {
        return children;
      }

      return '';
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-link';
    },
    processNode(node, children) {
      let linktype;
      if (node.attribs.linktype && node.attribs.linktype === 'window') {
        linktype = 'window';
      }
      return (
        <ProductLink
          displayType={type}
          product={product}
          linkType={linktype}
          key={product.id}
        >
          {children}
        </ProductLink>
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-title';
    },
    processNode(node) {
      let hasLink = true;
      if (node.attribs.haslink && node.attribs.haslink === 'false') {
        hasLink = false;
      }
      return (
        <ProductTitle
          key={product.id}
          displayType={type}
          product={product}
          hasLink={hasLink}
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-image';
    },
    processNode(node) {
      let hasLink = true;
      if (node.attribs.haslink && node.attribs.haslink === 'false') {
        hasLink = false;
      }
      const noImgTag = node.attribs.noimgtag === 'true' || false;
      const image =
        (node.attribs.image && parseInt(node.attribs.image, 10)) || 1;
      return (
        <Image
          displayType={type}
          product={product}
          hasLink={hasLink}
          noImageTag={noImgTag}
          size={node.attribs.size}
          index={image - 1}
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-add-to-cart';
    },
    processNode(node) {
      const hidePrice = node.attribs.hideprice === 'true' || false;
      const showVariantTitle =
        node.attribs.showvarianttitle === 'true' || false;
      const analytics = {
        viewType: type
      };
      if (analytics.type !== 'Detail') {
        analytics.position = productIndex;
        analytics.totalProducts = productCount;
      }
      return (
        <AddToCart
          product={product}
          showVariantTitle={showVariantTitle}
          hidePrice={hidePrice}
          analytics={analytics}
          settings={settings}
          key={product.id}
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-add-case-to-cart';
    },
    processNode(node) {
      const hidePrice = node.attribs.hideprice === 'true' || false;
      const analytics = {
        viewType: type
      };
      if (analytics.type !== 'Detail') {
        analytics.position = productIndex;
        analytics.totalProducts = productCount;
      }
      return (
        <AddCaseToCart
          product={product}
          hidePrice={hidePrice}
          settings={settings}
          key={product.id}
          analytics={analytics}
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-content';
    },
    processNode() {
      return <div dangerouslySetInnerHTML={{ __html: product.content }} />;
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-event-date';
    },
    processNode() {
      return (
        <EventDate
          key={`date-${product.id}`}
          product={product}
          settings={settings}
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-event-time';
    },
    processNode() {
      return (
        <EventTime
          key={`time-${product.id}`}
          product={product}
          settings={settings}
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name.substring(0, 15) === 'c7-product-meta';
    },
    processNode(node) {
      const code = node.name.replace('c7-product-meta-', '');
      if (code) {
        const metaData = product.metaData && product.metaData[code];
        if (metaData) {
          if (node.attribs && node.attribs.type === 'html') {
            return (
              <div key={code} dangerouslySetInnerHTML={{ __html: metaData }} />
            );
          }
          return metaData;
        }
      } else {
        return <div>{node.name}</div>;
      }
      return null;
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-allocation';
    },
    processNode() {
      return <Allocations product={product} />;
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-wine';
    },
    processNode() {
      return <Wine product={product} />;
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-price';
    },
    processNode() {
      return <Price variant={product.variants[0]} settings={settings} />;
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-product-compare-at-price';
    },
    processNode() {
      return (
        <CompareAtPrice variant={product.variants[0]} settings={settings} />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name.substring(0, 10) === 'c7-product';
    },
    processNode(node) {
      switch (node.name) {
        case 'c7-product-type': {
          return product.type;
        }
        case 'c7-product-teaser': {
          return product.teaser;
        }
        case 'c7-product-subtitle': {
          return product.subTitle;
        }
        case 'c7-product-wine-vintage': {
          return product.wine && product.wine.vintage;
        }
        case 'c7-product-wine-type': {
          return product.wine && product.wine.type;
        }
        case 'c7-product-wine-varietal': {
          return product.wine && product.wine.varietal;
        }
        case 'c7-product-wine-country': {
          return (
            product.wine &&
            product.wine.countryCode &&
            countryName(product.wine.countryCode)
          );
        }
        case 'c7-product-wine-region': {
          return product.wine && product.wine.region;
        }
        case 'c7-product-wine-appellation': {
          return product.wine && product.wine.appellation;
        }
        default: {
          return <div>{node.name}</div>;
        }
      }
    }
  },
  {
    shouldProcessNode() {
      return true;
    },
    processNode: processNodeDefinitions.processDefaultNode
  }
];

export const processingImages = (product, imageIndex) => [
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-image';
    },
    processNode(node) {
      let hasLink = true;
      if (node.attribs.haslink && node.attribs.haslink === 'false') {
        hasLink = false;
      }
      const noImgTag = node.attribs.noimgtag === 'true' || false;
      return (
        <Image
          displayType="List"
          product={product}
          hasLink={hasLink}
          noImageTag={noImgTag}
          size={node.attribs.size}
          index={imageIndex}
        />
      );
    }
  },
  {
    shouldProcessNode() {
      return true;
    },
    processNode: processNodeDefinitions.processDefaultNode
  }
];

export const processingList = (products, collection, type, settings) => [
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-products';
    },
    processNode(node, children) {
      const template = ReactDOMServer.renderToStaticMarkup(children);
      const newTemplate = template.replace(/c7not/g, '!');
      return (
        <ProductArray
          key={node.attribs.start || 'key'}
          start={node.attribs.start}
          end={node.attribs.end}
          products={products}
          settings={settings}
          type={type}
          processingInstructions={processingInstructions}
        >
          {newTemplate}
        </ProductArray>
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-collection-title';
    },
    processNode() {
      return collection.title;
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-collection-content';
    },
    processNode() {
      return (
        <div
          dangerouslySetInnerHTML={{ __html: collection.content }}
          key="collection-content"
        />
      );
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-collection-filter';
    },
    processNode(node) {
      if (type !== 'List') {
        return (
          <div key="list">
            No filters allowed on old dynamic collections of this style
          </div>
        );
      }
      switch (node.attribs.filter) {
        case 'wineType':
          return <WineTypeFilter key="wineType" />;
        case 'wineVarietal':
          return <WineVarietalFilter key="wineVarietal" />;
        case 'wineCountry':
          return <WineCountryCodeFilter key="wineCountry" />;
        case 'wineRegion':
          return <WineRegionFilter key="wineRegion" />;
        case 'wineAppellation':
          return <WineAppellationFilter key="wineAppellation" />;
        case 'vendor':
          return <VendorFilter key="vendor" />;
        case 'price':
          return <PriceFilter key="priceFilter" range={node.attribs.range} />;

        default:
          return node.name;
      }
    }
  },
  {
    replaceChildren: false,
    shouldProcessNode(node) {
      return node.name && node.name === 'c7-collection-product-count';
    },
    processNode() {
      if (type !== 'List') {
        return (
          <div key="list">No product count available on none-list page</div>
        );
      }
      return <CollectionProductCount collection={collection} />;
    }
  },
  {
    shouldProcessNode() {
      return true;
    },
    processNode: processNodeDefinitions.processDefaultNode
  }
];

export const addClosingTags = (template) => {
  let newTemplate = template;

  // Replace any closing C7 tags that might have been added;
  // newTemplate = newTemplate.replace(/<\/c7-product([\s\S]*?)>/g, '');
  newTemplate = newTemplate.replace(
    /<\/c7-(?!if|product-link|product-images|products)([\s\S]*?)>/g,
    ''
  );
  // Add Closing tags to C7, leaving the <c7-if> tags
  const c7Tags = newTemplate.match(
    /<c7-(?!if|product-link|product-images|products)([\s\S]*?)>/g
  );
  if (Array.isArray(c7Tags)) {
    const uniqueC7Tags = c7Tags.filter((it, i, ar) => ar.indexOf(it) === i);

    if (uniqueC7Tags) {
      uniqueC7Tags.forEach((element) => {
        const tag = element.match(/<([^\s>]+)(\s|>)+/)[1];
        newTemplate = newTemplate.replace(
          new RegExp(element, 'g'),
          `${element}</${tag}>`
        );
      });
    }
  }
  return newTemplate;
};

export const changeNot = (template) => {
  const newTemplate = template.replace(/<c7-if !/g, '<c7-if c7not');
  return newTemplate;
};

const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);

const doesAttributeEqual = (attribute, value, product) => {
  if (attribute.substring(0, 12) === 'product-meta') {
    const code = attribute.substring(13);
    const doesExist = product.metaData && product.metaData[code];
    if (doesExist === value) {
      return true;
    }
    return false;
  }

  //
  const productValue = attributeToValue(attribute, product);
  if (productValue === value) {
    return true;
  }
  return false;
};

const doesAttributeExist = (attribute, product) => {
  // Test for Meta
  if (attribute.substring(0, 12) === 'product-meta') {
    const code = attribute.substring(13);
    const doesExist = product.metaData && product.metaData[code];
    if (doesExist) {
      return true;
    }
    return false;
  }
  //
  const value = attributeToValue(attribute, product);
  if (value) {
    return true;
  }
  return false;
};

const doesAttributeNotExist = (attribute, product) => {
  // Test for Meta
  const newAttribute = attribute.substring(1);
  if (newAttribute.substring(0, 12) === 'product-meta') {
    const code = newAttribute.substring(13);
    const doesExist = product.metaData && product.metaData[code];
    if (doesExist) {
      return false;
    }
    return true;
  }
  //
  const value = attributeToValue(newAttribute, product);
  if (value) {
    return false;
  }
  return true;
};

const doesAttributeNotEqual = (attribute, value, product) => {
  const newAttribute = attribute.substring(1);
  if (newAttribute.substring(0, 12) === 'product-meta') {
    const code = newAttribute.substring(13);
    const doesExist = product.metaData && product.metaData[code];
    if (doesExist === value) {
      return false;
    }
    return true;
  }

  //
  const productValue = attributeToValue(newAttribute, product);
  if (productValue === value) {
    return false;
  }
  return true;
};

const attributeToValue = (attribute, product) => {
  switch (attribute) {
    case 'product-title':
      return product.title;
    case 'product-subtitle':
      return product.subTitle;
    case 'product-type':
      return product.type;
    case 'product-image':
      return product.image || product.images?.[0]?.src;
    case 'product-teaser':
      return product.teaser;
    case 'product-content':
      return product.content;
    case 'product-wine':
      return product.wine;
    case 'product-wine-type':
      return product.wine && product.wine.type;
    case 'product-wine-varietal':
      return product.wine && product.wine.varietal;
    case 'product-wine-country':
      return product.wine && product.wine.country;
    case 'product-wine-region':
      return product.wine && product.wine.region;
    case 'product-wine-appellation':
      return product.wine && product.wine.appellation;
    case 'product-wine-vintage':
      return product.wine && product.wine.vintage;
    default:
      return undefined;
  }
};
